import { useEffect, useRef, useState } from "react";

/**
 * The signature visual: a 500ms heartbeat strip pinned under the nav.
 * One bar per round; bars flow leftward; the latest is bright emerald,
 * older bars fade. The center label flashes the round_id on each tick.
 *
 * For the scaffold pass, this runs against a local interval at the
 * canonical 500ms ROUND_WINDOW_MS so it animates without the API.
 * The next pass wires it to /v1/stream (SSE) for real round_ids.
 */
export function Heartbeat() {
  const [round, setRound] = useState<number>(() => Math.floor(Date.now() / 500));
  const [pulse, setPulse] = useState(0);
  const lastRef = useRef(round);

  useEffect(() => {
    const id = setInterval(() => {
      const r = Math.floor(Date.now() / 500);
      if (r !== lastRef.current) {
        lastRef.current = r;
        setRound(r);
        setPulse((p) => p + 1);
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  const bars = 60;

  return (
    <div className="relative z-10 border-y border-border bg-bg-deep/60 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-2 sm:px-10">
        <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-m40">
          HEARTBEAT
        </span>

        <div className="relative flex flex-1 items-center gap-[3px] overflow-hidden">
          {Array.from({ length: bars }).map((_, i) => {
            const distance = bars - 1 - i;
            const intensity = Math.max(0, 1 - distance / bars);
            const isLatest = distance === 0;
            return (
              <span
                key={`${i}-${isLatest ? pulse : 0}`}
                aria-hidden
                className="h-[10px] w-[3px] rounded-[1px] transition-colors duration-300"
                style={{
                  background: isLatest
                    ? "var(--color-emerald)"
                    : `rgba(127, 255, 140, ${(intensity * 0.55).toFixed(3)})`,
                  boxShadow: isLatest
                    ? `0 0 8px rgba(127,255,140,0.65)`
                    : undefined,
                  transform: `scaleY(${(0.4 + intensity * 0.6).toFixed(3)})`,
                  animation: isLatest ? "hb-pop 240ms var(--ease-out-quint)" : undefined,
                }}
              />
            );
          })}
        </div>

        <span className="font-mono text-[10px] tabular text-m70">
          R<span className="text-emerald">{round.toLocaleString("en-US")}</span>
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-m40">
          500ms
        </span>
      </div>
    </div>
  );
}
